import { useCallback } from 'react';
import { openDB, IDBPDatabase } from 'idb';
import * as pdfjsLib from 'pdfjs-dist';
import getApiHeaders from '../helpers/apiHeaders.js';
import IncrementalMermaidParser from '../helpers/parseMermaidIncremental';

// Set up the worker for pdfjs
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const DB_NAME = 'pdfProcessor';
const STORE_NAME = 'pdfs';
const DB_VERSION = 1;

const getOpenDB = () => openDB(DB_NAME, DB_VERSION, {
  upgrade(db) {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.createObjectStore(STORE_NAME, { keyPath: 'id' });
    }
  },
});

/**
 * Run a callback with a fresh IndexedDB connection, then close it.
 * Prevents iOS Safari stale connection / blocked transaction issues.
 */
const withDb = async <T>(callback: (db: IDBPDatabase) => Promise<T>): Promise<T> => {
  const db = await getOpenDB();
  try {
    return await callback(db);
  } finally {
    db.close();
  }
};

interface PageData {
  pageNumber: number;
  text?: string;
  processedAt?: string;
  embedding?: number[];
}

interface PdfRecord {
  id: string;
  blob?: ArrayBuffer;
  fileName?: string;
  timestamp?: number;
  pages?: PageData[];
  tutorState?: TutorState;
}

interface TutorState {
  currentTopic: string | null;
  currentLevel: number;
  topics: Record<string, any>;
  lastExplanationTopic: string | null;
  awaitingAnswer: boolean;
  lastCheckQuestion: string | null;
  lastIdealAnswer: string | null;
  lastRetrievedPages: any[];
}

interface ProcessPdfResult {
  fileId: string;
  fileName: string;
}

interface PdfFileInfo {
  id: string;
  fileName: string;
  timestamp: number;
}

const usePdfProcessor = () => {
  const savePdfToIndexedDB = async (file: File, fileId: string): Promise<void> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdfData: PdfRecord = {
      id: fileId,
      blob: arrayBuffer,
      fileName: file.name,
      timestamp: Date.now(),
    };
    await withDb(async (db) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      await store.put(pdfData);
      await tx.done;
    });
  };

  const extractTextFromPage = async (pdf: pdfjsLib.PDFDocumentProxy, pageNumber: number): Promise<string> => {
    try {
      const page = await pdf.getPage(pageNumber);
      const textContent = await page.getTextContent();
      const textItems = textContent.items.map((item: any) => item.str);
      const text = textItems.join(' ');
      return text;
    } catch (error) {
      console.error(`[PDF Processor] Error extracting text from page ${pageNumber}:`, error);
      throw error;
    }
  };

  const generateFileId = (): string => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  /**
   * Process PDF file - generates file UUID + page UUIDs, saves blob
   */
  const processPdf = async (file: File): Promise<ProcessPdfResult> => {
    const localFileId = generateFileId();

    // Save PDF blob to IndexedDB
    await savePdfToIndexedDB(file, localFileId);

    return {
      fileId: localFileId,
      fileName: file.name,
    };
  };

  // Initialize tutor state for a file
  const initializeTutorState = useCallback((): TutorState => {
    return {
      currentTopic: null,
      currentLevel: 0,
      topics: {},
      lastExplanationTopic: null,
      awaitingAnswer: false,
      lastCheckQuestion: null,
      lastIdealAnswer: null,
      lastRetrievedPages: [],
    };
  }, []);

  // Get tutor state for a file
  const getTutorState = useCallback(async (fileId: string): Promise<TutorState> => {
    return withDb(async (db) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const fileData = await store.get(fileId) as PdfRecord | undefined;
        await tx.done;

        if (fileData && fileData.tutorState) {
          return fileData.tutorState;
        }
        return initializeTutorState();
      } catch (error) {
        console.error('Error getting tutor state:', error);
        return initializeTutorState();
      }
    });

  }, [initializeTutorState]);

  // Update tutor state for a file
  const updateTutorState = useCallback(async (fileId: string, updates: Partial<TutorState>): Promise<void> => {
    return withDb(async (db) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const fileData = await store.get(fileId) as PdfRecord | undefined;
        await tx.done;

        if (fileData) {
          if (!fileData.tutorState) {
            fileData.tutorState = initializeTutorState();
          }
          fileData.tutorState = {
            ...fileData.tutorState,
            ...updates,
          };

          const updateTx = db.transaction(STORE_NAME, 'readwrite');
          const updateStore = updateTx.objectStore(STORE_NAME);
          await updateStore.put(fileData);
          await updateTx.done;
        }
      } catch (error) {
        console.error('Error updating tutor state:', error);
      }
    });

  }, [initializeTutorState]);

  /**
   * List all PDF files stored in IndexedDB (id and fileName only, sorted by timestamp desc).
   */
  const getAllPdfFiles = useCallback(async (): Promise<PdfFileInfo[]> => {
    return withDb(async (db) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const all = await store.getAll() as PdfRecord[];
        await tx.done;
        return all
          .map(({ id, fileName, timestamp }) => ({ id, fileName: fileName || id, timestamp: timestamp || 0 }))
          .sort((a, b) => b.timestamp - a.timestamp);
      } catch (error) {
        console.error('[PDF Processor] Error listing files:', error);
        return [];
      }
    });

  }, []);

  /**
   * Delete a PDF file from IndexedDB by id.
   */
  const deletePdfFile = useCallback(async (fileId: string): Promise<void> => {
    if (!fileId) return;
    await withDb(async (db) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      await store.delete(fileId);
      await tx.done;
    });

  }, []);

  /**
   * Estimate token count from text length (~4 chars per token).
   */
  const estimateTokens = (text: string): number => Math.ceil(text.length / 4);

  /**
   * Process all pages with a running summary to reduce node explosion.
   * Groups multiple pages into chunks of ~100K tokens to maximize LLM context
   * usage and reduce the number of API calls.
   *
   * onPageProcessed is called after each chunk with the chunk's graph,
   * so the caller can merge it into the unified graph incrementally.
   */
  const processAllPages = async (
    fileId: string,
    onPageProcessed: (pageIndex: number, totalPages: number, result: any) => void,
    onStreamChunk?: (partialGraph: { nodes: any[]; edges: any[] }) => void,
    onLog?: (entry: string) => void,
    onEmbeddingProgress?: (event: { step: string; current?: number; total?: number }) => void,
  ): Promise<void> => {
    const item = await withDb(async (db) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const result = await store.get(fileId) as PdfRecord | undefined;
      await tx.done;
      return result;
    });

    if (!item?.blob) throw new Error(`Blob not found for fileId: ${fileId}`);

    const constants = (await import('../helpers/constants')).default;

    // Reuse cached page texts if available, otherwise extract from PDF blob
    let pageTexts: string[];
    if (item.pages && item.pages.length > 0 && item.pages.every(p => p.text)) {
      pageTexts = item.pages.map(p => p.text!);
    } else {
      const pdf = await pdfjsLib.getDocument({ data: item.blob }).promise;
      const totalPages = pdf.numPages;
      pageTexts = [];
      for (let i = 1; i <= totalPages; i++) {
        pageTexts.push(await extractTextFromPage(pdf, i));
      }
      // Cache extracted texts for future rebuilds
      await withDb(async (db) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const record = await store.get(fileId) as PdfRecord | undefined;
        if (record) {
          record.pages = pageTexts.map((text, i) => ({ pageNumber: i + 1, text }));
          await store.put(record);
        }
        await tx.done;
      });
    }
    const totalPages = pageTexts.length;

    // Fire off embedding in parallel with graph building (SSE stream for progress)
    const embeddingPayload = pageTexts.map((text, i) => ({ pageNumber: i + 1, text }));
    (async () => {
      try {
        const embeddingRes = await fetch(constants.fetchFileEmbed, {
          method: 'POST',
          headers: getApiHeaders(),
          credentials: 'include',
          body: JSON.stringify({ fileId, pages: embeddingPayload }),
        });
        if (!embeddingRes.ok || !embeddingRes.body) return;
        const reader = embeddingRes.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop() || '';
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'progress' && onEmbeddingProgress) {
                onEmbeddingProgress(event);
              } else if (event.type === 'done' && onEmbeddingProgress) {
                onEmbeddingProgress({ step: 'done', total: event.chunksProcessed });
              } else if (event.type === 'error') {
                console.error('[PDF Processor] Embedding error:', event.error);
              }
            } catch (e) { /* skip malformed events */ }
          }
        }
      } catch (err) {
        console.error('[PDF Processor] Embedding failed:', err);
      }
    })();

    // gpt-4.1-mini has 1M context. We cap content per chunk so the LLM
    // responds in reasonable time and doesn't timeout on mobile.
    const MAX_CONTENT_TOKENS = 900_000;      // hard cap per chunk
    const PROMPT_OVERHEAD_TOKENS = 2_000;    // prompt template + groups
    const RESPONSE_RESERVE_TOKENS = 16_000;  // max output tokens
    const MODEL_CONTEXT_LIMIT = 1_000_000;

    let previousMermaid = '';
    let previousGroups: string[] = [];
    let pageIdx = 0;

    while (pageIdx < pageTexts.length) {
      // Dynamic budget: subtract previousMermaid, but cap at MAX_CONTENT_TOKENS
      const mermaidTokens = estimateTokens(previousMermaid);
      const contextAvailable = MODEL_CONTEXT_LIMIT - PROMPT_OVERHEAD_TOKENS - RESPONSE_RESERVE_TOKENS - mermaidTokens;
      const contentTokenBudget = Math.min(MAX_CONTENT_TOKENS, contextAvailable);
      const charBudget = contentTokenBudget * 4;

      // Build chunk from remaining pages up to the budget
      let chunkContent = '';
      const chunkPageNumbers: number[] = [];
      const chunkStartPage = pageIdx + 1;

      while (pageIdx < pageTexts.length) {
        const pageNum = pageIdx + 1;
        const separator = `--- Page ${pageNum} ---\n`;
        const pageEntry = separator + pageTexts[pageIdx];

        if (chunkContent.length > 0 && chunkContent.length + pageEntry.length > charBudget) {
          break; // this page doesn't fit, start a new chunk
        }

        chunkContent += (chunkContent.length > 0 ? '\n' : '') + pageEntry;
        chunkPageNumbers.push(pageNum);
        pageIdx++;
      }

      const chunkEndPage = chunkPageNumbers[chunkPageNumbers.length - 1];
      const chunkLabel = chunkStartPage === chunkEndPage
        ? `p${chunkStartPage}`
        : `p${chunkStartPage}-${chunkEndPage}`;

      console.log(`[PDF Processor] Sending chunk ${chunkLabel} (~${estimateTokens(chunkContent)} content tokens, ~${mermaidTokens} mermaid tokens, budget: ${contentTokenBudget} tokens)`);

      const response = await fetch(constants.fileExtractUrl, {
        method: 'POST',
        headers: getApiHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          pageContent: chunkContent,
          startPage: chunkStartPage,
          endPage: chunkEndPage,
          previousMermaid,
          previousGroups,
        }),
      });

      if (!response.ok) {
        if (response.status === 429) {
          let errorMessage = 'Rate limit exceeded';
          try {
            const data = await response.json();
            errorMessage = data.error || data.message || errorMessage;
          } catch (e) {
            console.error('[PDF Processor] Failed to parse rate limit response:', e);
          }
          const error = new Error(errorMessage) as Error & { status?: number };
          error.status = 429;
          throw error;
        }
        console.error(`[PDF Processor] Failed to extract ${chunkLabel}: ${response.status}`);
        continue;
      }

      // Read SSE stream from backend, parsing mermaid lines incrementally
      let result: any = null;
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const mermaidParser = onStreamChunk ? new IncrementalMermaidParser() : null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse complete SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // keep incomplete line in buffer
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'done') {
                result = event;
              } else if (event.type === 'timing') {
                // timing events logged on canvas overlay
              } else if (event.type === 'error') {
                console.error(`[PDF Processor] Stream error for ${chunkLabel}:`, event.error);
              } else if (event.type === 'chunk') {
                onLog?.(event.content);
                if (mermaidParser) {
                  const partialGraph = mermaidParser.append(event.content);
                  if (partialGraph) {
                    onStreamChunk!(partialGraph);
                    // Wait longer than the 50ms debounce in useHandleGraphUpdates
                    // so each node actually gets rendered before the next one
                    await new Promise(r => setTimeout(r, 80));
                  }
                }
              }
            } catch (e) {
              console.error(`[PDF Processor] Failed to parse SSE event for ${chunkLabel}:`, e, 'Raw:', line.slice(6));
            }
          }
        }
      }

      if (!result) {
        console.error(`[PDF Processor] No result received for ${chunkLabel}`);
        continue;
      }

      // Carry the updated mermaid forward for the next chunk
      if (result.mermaid) {
        previousMermaid = result.mermaid;
      }

      // Accumulate group names for next chunk's context
      if (result.legend && Array.isArray(result.legend)) {
        for (const entry of result.legend) {
          if (entry.name && !previousGroups.includes(entry.name)) {
            previousGroups.push(entry.name);
          }
        }
      }

      // Report progress using the last page in the chunk
      onPageProcessed(chunkEndPage, totalPages, result);
    }
  };

  /**
   * Read text for specific pages from the PDF blob stored in IndexedDB.
   */
  const getPageTexts = useCallback(async (fileId: string, pageNumbers: number[]): Promise<string> => {
    const item = await withDb(async (db) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const result = await store.get(fileId) as PdfRecord | undefined;
      await tx.done;
      return result;
    });

    if (!item?.blob) return '';

    const pdf = await pdfjsLib.getDocument({ data: item.blob }).promise;
    const sorted = [...pageNumbers].sort((a, b) => a - b);
    const parts: string[] = [];

    for (const pageNum of sorted) {
      if (pageNum >= 1 && pageNum <= pdf.numPages) {
        const text = await extractTextFromPage(pdf, pageNum);
        parts.push(`--- Page ${pageNum} start ---\n${text}\n--- Page ${pageNum} end ---`);
      }
    }

    return parts.join('\n\n');
  }, []);

  return {
    processPdf,
    processAllPages,
    getPageTexts,
    getTutorState,
    updateTutorState,
    initializeTutorState,
    getAllPdfFiles,
    deletePdfFile,
  };
};

export default usePdfProcessor;
